import { ArrowRight, Network, Phone, UserCheck } from "lucide-react";
import { Eyebrow, ArrowCta, Reveal, MaskLineView, VerdeSpark } from "../components/ui-bits";
import { PHONE_DISPLAY, PHONE_TEL } from "../components/Navbar";
import { useLang } from "../i18n/LangContext";

const COPY = {
  en: {
    heroEyebrow: "About Verde Capital",
    headline: "About",
    heroCopy:
      "Verde Capital is led by an experienced team with deep real estate and finance expertise in Portugal and internationally. We combine local market knowledge with AI to underwrite opportunities more efficiently — while maintaining a disciplined, hands-on approach.",
    whyEyebrow: "Why Verde Capital exists",
    whyH: "The gap between the land and the loan.",
    why1: "Every experienced developer knows the period where good projects require capital but traditional bank financing is not yet available — land acquired, licensing underway, pre-sales building.",
    why2: "Verde Capital exists to finance that period. Short-term senior facilities of €1–5M, designed around the developer's journey, delivered by AI and judged by experienced underwriters.",
    why3: "We know that position first-hand — we've been there ourselves: acquiring land, moving projects through licensing, and waiting to close the financing gap to a bank loan.",
    workEyebrow: "How we work",
    principles: [
      { t: "AI-enabled", c: "AI and modern tooling organise information and accelerate underwriting." },
      { t: "Expert-underwritten", c: "Experienced humans and local specialists make the credit decisions." },
      { t: "Local", c: "One market, understood deeply: Portugal." },
      { t: "Long-term", c: "Facilities that grow with a developer's pipeline, project by project." },
    ],
    decideEyebrow: "How we decide",
    decideH1: "Three layers behind",
    decideH2: "every credit decision.",
    decideCopy: "No decision is made by a model alone — and none is made without one.",
    decideLink: "Meet our expert network",
    deciders: [
      { icon: VerdeSpark, t: "Verde — our AI underwriter", d: "Organises information, analyses planning, comparables and market data, and builds each case for review." },
      { icon: UserCheck, t: "An internal credit team", d: "Experienced professionals assess structure, risk and terms — and make every final decision." },
      { icon: Network, t: "A verified expert network", d: "Local planning, legal, technical, valuation and market specialists ground each decision in the places where projects get built." },
    ],
    briefEyebrow: "Verde Capital in brief",
    briefH: "The facts:",
    briefCopy: "Who we are, what we lend and on what terms — stated once, clearly.",
    facts: [
      { k: "Lending entity", v: "Verde Partner Capital B.V." },
      { k: "Registration", v: "KvK 42140709" },
      { k: "Registered office", v: "Commelinstraat 21-1, 1093 TG Amsterdam, The Netherlands" },
      { k: "Platform", v: "Part of Verde 2050 — an independent real estate investment platform" },
      { k: "Focus", v: "Portuguese residential real estate" },
      { k: "Typical facilities", v: "€1–5m" },
      { k: "Terms", v: "6 – 24 months, senior-secured" },
      { k: "Indicative terms", v: "Within 24 hours" },
      { k: "Security", v: "First-ranking mortgage over the asset" },
    ],
    focusEyebrow: "Portugal focus",
    focusCopy: "Verde Capital underwrites in Portugal, for Portugal. Local planning knowledge, local market data and the Verde Capital Expert Network keep every decision grounded in the places where projects actually get built.",
    visionEyebrow: "Long-term vision",
    visionCopy: "Start with one €1–5M facility. Build repeat projects, developer relationships, and larger multi-project facilities over time. Capital for a better built world.",
    ctaH: "Capital that unlocks development.",
    ctaApply: "Get indicative terms",
    ctaContact: "Contact us",
  },
  pt: {
    heroEyebrow: "Sobre a Verde Capital",
    headline: "Sobre nós",
    heroCopy:
      "A Verde Capital é liderada por uma equipa experiente, com profundo conhecimento imobiliário e financeiro em Portugal e a nível internacional. Combinamos conhecimento local do mercado com IA para subscrever oportunidades de forma mais eficiente — mantendo uma abordagem disciplinada e próxima.",
    whyEyebrow: "Porque existe a Verde Capital",
    whyH: "A distância entre o terreno e o crédito.",
    why1: "Todo promotor experiente conhece o período em que bons projetos precisam de capital, mas o financiamento bancário tradicional ainda não está disponível — terreno adquirido, licenciamento em curso, pré-vendas a crescer.",
    why2: "A Verde Capital existe para financiar esse período. Operações séniores de curto prazo de €1–5M, desenhadas em torno da jornada do promotor, aceleradas por IA e avaliadas por subscritores experientes.",
    why3: "Conhecemos essa posição em primeira mão — já lá estivemos: a adquirir terreno, a levar projetos pelo licenciamento e à espera de colmatar a distância até ao crédito bancário.",
    workEyebrow: "Como trabalhamos",
    principles: [
      { t: "Com apoio de IA", c: "A IA e ferramentas modernas organizam a informação e aceleram a subscrição." },
      { t: "Subscrito por especialistas", c: "Pessoas experientes e especialistas locais tomam as decisões de crédito." },
      { t: "Local", c: "Um mercado, compreendido a fundo: Portugal." },
      { t: "Longo prazo", c: "Operações que crescem com o pipeline do promotor, projeto a projeto." },
    ],
    decideEyebrow: "Como decidimos",
    decideH1: "Três camadas por trás",
    decideH2: "de cada decisão de crédito.",
    decideCopy: "Nenhuma decisão é tomada apenas por um modelo — e nenhuma é tomada sem um.",
    decideLink: "Conheça a nossa rede de especialistas",
    deciders: [
      { icon: VerdeSpark, t: "Verde — a nossa subscritora de IA", d: "Organiza a informação, analisa licenciamento, comparáveis e dados de mercado, e constrói cada processo para revisão." },
      { icon: UserCheck, t: "Uma equipa de crédito interna", d: "Profissionais experientes avaliam estrutura, risco e condições — e tomam cada decisão final." },
      { icon: Network, t: "Uma rede de especialistas verificada", d: "Especialistas locais de planeamento, legal, técnico, avaliação e mercado fundamentam cada decisão nos locais onde os projetos são construídos." },
    ],
    briefEyebrow: "Verde Capital em resumo",
    briefH: "Os factos:",
    briefCopy: "Quem somos, o que emprestamos e em que condições — dito uma vez, com clareza.",
    facts: [
      { k: "Entidade de crédito", v: "Verde Partner Capital B.V." },
      { k: "Registo", v: "KvK 42140709" },
      { k: "Escritório registado", v: "Commelinstraat 21-1, 1093 TG Amsterdão, Países Baixos" },
      { k: "Plataforma", v: "Parte da Verde 2050 — uma plataforma independente de investimento imobiliário" },
      { k: "Foco", v: "Imobiliário residencial português" },
      { k: "Operações típicas", v: "€1–5m" },
      { k: "Prazos", v: "6 – 24 meses, com garantia sénior" },
      { k: "Condições indicativas", v: "Em 24 horas" },
      { k: "Garantia", v: "Hipoteca de primeiro grau sobre o ativo" },
    ],
    focusEyebrow: "Foco em Portugal",
    focusCopy: "A Verde Capital subscreve em Portugal, para Portugal. Conhecimento local de planeamento, dados de mercado locais e a Rede de Especialistas da Verde Capital mantêm cada decisão alicerçada nos locais onde os projetos são realmente construídos.",
    visionEyebrow: "Visão de longo prazo",
    visionCopy: "Começar com uma operação de €1–5M. Construir projetos recorrentes, relações com promotores e operações maiores multi-projeto ao longo do tempo. Capital para um mundo construído melhor.",
    ctaH: "Capital que desbloqueia o desenvolvimento.",
    ctaApply: "Obter condições indicativas",
    ctaContact: "Contacte-nos",
  },
  nl: {
    heroEyebrow: "Over Verde Capital",
    headline: "Over ons",
    heroCopy:
      "Verde Capital wordt geleid door een ervaren team met diepe vastgoed- en financiële kennis van Portugal en daarbuiten. Wij combineren lokale marktkennis met AI om kansen efficiënter te beoordelen — met een gedisciplindeerde, persoonlijke aanpak.",
    whyEyebrow: "Waarom Verde Capital bestaat",
    whyH: "De afstand tussen grond en krediet.",
    why1: "Elke ervaren ontwikkelaar kent de periode waarin goede projecten kapitaal nodig hebben, maar traditionele bankfinanciering nog niet beschikbaar is — grond gekocht, vergunningen lopen, voorverkoop groeit.",
    why2: "Verde Capital bestaat om die periode te financieren. Kortlopende senior transacties van €1–5 mln, ontworpen rond de reis van de ontwikkelaar, versneld door AI en beoordeeld door ervaren underwriters.",
    why3: "Wij kennen die positie uit eigen ervaring — wij zaten er zelf: grond kopend, projecten door vergunningen loodsend, wachtend om de afstand tot bankkrediet te overbruggen.",
    workEyebrow: "Hoe wij werken",
    principles: [
      { t: "AI-ondersteund", c: "AI en moderne tools ordenen informatie en versnellen de beoordeling." },
      { t: "Beoordeeld door experts", c: "Ervaren mensen en lokale experts nemen de kredietbeslissingen." },
      { t: "Lokaal", c: "Eén markt, grondig begrepen: Portugal." },
      { t: "Langetermijn", c: "Transacties die meegroeien met de pijplijn van de ontwikkelaar, project voor project." },
    ],
    decideEyebrow: "Hoe wij beslissen",
    decideH1: "Drie lagen achter",
    decideH2: "elke kredietbeslissing.",
    decideCopy: "Geen beslissing wordt alleen door een model genomen — en geen enkele zonder.",
    decideLink: "Ontmoet ons expertennetwerk",
    deciders: [
      { icon: VerdeSpark, t: "Verde — onze AI-underwriter", d: "Ordent informatie, analyseert vergunningen, vergelijkingen en marktgegevens, en bouwt elk dossier ter beoordeling." },
      { icon: UserCheck, t: "Een intern kredietteam", d: "Ervaren professionals toetsen structuur, risico en voorwaarden — en nemen elke definitieve beslissing." },
      { icon: Network, t: "Een geverifieerd expertennetwerk", d: "Lokale specialisten in ruimtelijke ordening, juridisch, techniek, taxatie en markt onderbouwen elke beslissing waar projecten echt gebouwd worden." },
    ],
    briefEyebrow: "Verde Capital in het kort",
    briefH: "De feiten:",
    briefCopy: "Wie wij zijn, wat wij uitlenen en onder welke voorwaarden — één keer gezegd, met helderheid.",
    facts: [
      { k: "Kredietentiteit", v: "Verde Partner Capital B.V." },
      { k: "Registratie", v: "KvK 42140709" },
      { k: "Geregistreerd kantoor", v: "Commelinstraat 21-1, 1093 TG Amsterdam, Nederland" },
      { k: "Platform", v: "Onderdeel van Verde 2050 — een onafhankelijk vastgoedbeleggingsplatform" },
      { k: "Focus", v: "Portugese residentiële vastgoedontwikkeling" },
      { k: "Typische transacties", v: "€1–5 mln" },
      { k: "Looptijden", v: "6 – 24 maanden, met senior zekerheid" },
      { k: "Indicatieve voorwaarden", v: "Binnen 24 uur" },
      { k: "Zekerheid", v: "Eerste rangs hypothecair recht op het actief" },
    ],
    focusEyebrow: "Focus op Portugal",
    focusCopy: "Verde Capital beoordeelt in Portugal, voor Portugal. Lokale planningskennis, lokale marktgegevens en het Expertennetwerk van Verde Capital houden elke beslissing verankerd waar projecten echt gebouwd worden.",
    visionEyebrow: "Langetermijnvisie",
    visionCopy: "Beginnen met één transactie van €1–5 mln. In de loop van de tijd terugkerende projecten, relaties met ontwikkelaars en grotere multi-projecttransacties opbouwen. Kapitaal voor een beter gebouwde wereld.",
    ctaH: "Kapitaal dat ontwikkeling mogelijk maakt.",
    ctaApply: "Indicatieve voorwaarden aanvragen",
    ctaContact: "Neem contact op",
  },
};

export default function About() {
  const { lang, lp } = useLang();
  const c = COPY[lang];
  return (
    <div className="bg-cream">
      {/* ── HERO (DARK) ──────────────────────────────────── */}
      <section className="relative overflow-hidden bg-verde-950" data-testid="about-hero">
        <img
          src="/hero-builders.jpg"
          alt="Developers reviewing plans at a coastal site in Portugal"
          className="absolute inset-0 h-full w-full object-cover opacity-35"
          data-testid="about-hero-photo"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-verde-950 via-verde-950/80 to-verde-950/40" />
        <div className="container-x relative pt-32 sm:pt-40 pb-20 lg:pb-24">
          <div className="max-w-3xl">
            <Eyebrow light>{c.heroEyebrow}</Eyebrow>
            <h1 className="display mt-6 text-[2.6rem] sm:text-5xl lg:text-[56px] text-white" data-testid="page-headline">
              <MaskLineView>{c.headline}</MaskLineView>
            </h1>
            <Reveal delay={0.25} className="mt-7 max-w-2xl">
              <p className="text-lg leading-relaxed text-cream/85">
                {c.heroCopy}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── WHY WE EXIST ─────────────────────────────────── */}
      <section className="py-20 lg:py-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>{c.whyEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="about-why-headline">
              <MaskLineView>{c.whyH}</MaskLineView>
            </h2>
          </div>
          <Reveal delay={0.15} className="lg:col-span-7 space-y-5">
            <p className="text-lg text-ink leading-relaxed">{c.why1}</p>
            <p className="text-lg text-ink leading-relaxed">{c.why2}</p>
            <p className="text-lg leading-relaxed text-charcoal">{c.why3}</p>
          </Reveal>
        </div>
      </section>

      {/* ── HOW WE WORK ──────────────────────────────────── */}
      <section className="py-20 lg:py-28 bg-white border-y border-sand">
        <div className="container-x">
          <Eyebrow>{c.workEyebrow}</Eyebrow>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {c.principles.map((p, i) => (
              <Reveal key={p.t} delay={0.08 + i * 0.08}>
                <div className="card h-full p-7" data-testid={`principle-${i}`}>
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-verde-50 font-mono text-xs text-verde-800">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="mt-6 font-display text-lg font-bold text-charcoal">{p.t}</h3>
                  <p className="mt-2 text-sm text-ink leading-relaxed">{p.c}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── HOW WE DECIDE ────────────────────────────────── */}
      <section className="py-20 lg:py-28" data-testid="about-decide">
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>{c.decideEyebrow}</Eyebrow>
            <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="decide-headline">
              <MaskLineView>{c.decideH1}</MaskLineView>
              <MaskLineView delay={0.1}>{c.decideH2}</MaskLineView>
            </h2>
            <Reveal delay={0.2} className="mt-6 max-w-md">
              <p className="text-[15px] leading-relaxed text-fog">
                {c.decideCopy}
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-8">
              <a
                href={lp("/expert-network")}
                data-testid="decide-experts-link"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-verde-800 transition-colors hover:text-verde-700"
              >
                {c.decideLink}
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </Reveal>
          </div>
          <div className="lg:col-span-7 space-y-2">
            {c.deciders.map((d, i) => (
              <Reveal key={d.t} delay={0.1 + i * 0.08}>
                <div
                  className="group flex items-start gap-5 rounded-2xl p-6 transition-colors duration-300 hover:bg-white/70"
                  data-testid={`about-decider-${i}`}
                >
                  <span
                    className={`relative flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl ring-1 shadow-[0_14px_30px_-18px_rgba(7,39,29,0.45)] transition-transform duration-300 group-hover:-rotate-1 group-hover:scale-[1.06] ${
                      i === 0
                        ? "bg-gradient-to-br from-[#E9F3ED] to-[#D8E8DE] ring-verde-800/10 text-verde-800"
                        : i === 1
                          ? "bg-gradient-to-br from-[#EDF3EE] to-[#DDE9E0] ring-verde-800/10 text-verde-800"
                          : "bg-gradient-to-br from-[#F4EEE0] to-[#EADFC8] ring-brass/25 text-[#9A7B3F]"
                    }`}
                  >
                    <span className="absolute inset-0 bg-gradient-to-b from-white/60 via-transparent to-transparent" aria-hidden="true" />
                    <d.icon className="relative h-6 w-6" strokeWidth={i === 0 ? undefined : 1.6} aria-hidden="true" />
                  </span>
                  <div>
                    <h3 className="font-display text-lg font-bold text-charcoal">{d.t}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-fog">{d.d}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── IN BRIEF ─────────────────────────────────────── */}
      <section className="pb-20 lg:pb-28" data-testid="about-brief">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 rounded-2xl border border-sand bg-white p-6 sm:p-10 lg:items-center lg:p-14">
            <div className="lg:col-span-5">
              <Eyebrow>{c.briefEyebrow}</Eyebrow>
              <h2 className="display mt-5 text-3xl sm:text-4xl text-charcoal" data-testid="brief-headline">
                <MaskLineView>{c.briefH}</MaskLineView>
              </h2>
              <Reveal delay={0.2} className="mt-6 max-w-md">
                <p className="text-[15px] leading-relaxed text-fog">
                  {c.briefCopy}
                </p>
              </Reveal>
              <Reveal delay={0.3} className="mt-8">
                <a
                  href={PHONE_TEL}
                  data-testid="brief-phone-link"
                  className="inline-flex items-center gap-2.5 rounded-full border border-sand px-6 py-3 text-sm font-semibold text-charcoal transition-colors duration-300 hover:border-verde-700 hover:text-verde-800"
                >
                  <Phone className="h-4 w-4" /> {PHONE_DISPLAY}
                </a>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              <div className="overflow-hidden rounded-xl border border-sand" data-testid="brief-table">
                {c.facts.map((r, i) => (
                  <div
                    key={r.k}
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-4 px-5 py-4 text-sm ${i < c.facts.length - 1 ? "border-b border-sand" : ""}`}
                  >
                    <span className="text-fog">{r.k}</span>
                    <span className="font-medium text-charcoal">{r.v}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FOCUS + VISION ───────────────────────────────── */}
      <section className="pb-20 lg:pb-28">
        <div className="container-x grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="border-t border-charcoal/15 pt-6">
            <Eyebrow>{c.focusEyebrow}</Eyebrow>
            <Reveal delay={0.15}>
              <p className="mt-5 text-lg text-ink leading-relaxed">
                {c.focusCopy}
              </p>
            </Reveal>
          </div>
          <div className="border-t border-charcoal/15 pt-6">
            <Eyebrow>{c.visionEyebrow}</Eyebrow>
            <Reveal delay={0.2}>
              <p className="mt-5 text-lg text-ink leading-relaxed">
                {c.visionCopy}
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── CTA BAND ─────────────────────────────────────── */}
      <section className="bg-verde-950 text-cream py-20 lg:py-28 grain relative">
        <div className="container-x relative flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
          <h2 className="display text-3xl sm:text-4xl lg:text-5xl text-white max-w-xl">
            <MaskLineView>{c.ctaH}</MaskLineView>
          </h2>
          <Reveal delay={0.15} className="flex flex-wrap gap-3.5">
            <ArrowCta to={lp("/apply")} variant="light">{c.ctaApply}</ArrowCta>
            <ArrowCta to={lp("/contact")} variant="ghost-light">{c.ctaContact}</ArrowCta>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
